/**
 * URL Metadata Service
 * Fetches and caches URL metadata (title, favicon) for rich link previews.
 */

import { apiService as api } from './api.service';

export interface UrlMetadata {
  url: string;
  title: string | null;
  description: string | null;
  favicon: string | null;
  domain: string;
}

interface CacheEntry {
  metadata: UrlMetadata;
  expiresAt: number;
}

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour client-side cache
const cache = new Map<string, CacheEntry>();

// Track in-flight requests to avoid duplicate fetches
const pendingRequests = new Map<string, Promise<UrlMetadata>>();

/**
 * Fetch metadata for a URL with caching and deduplication.
 */
export async function fetchUrlMetadata(url: string): Promise<UrlMetadata> {
  // Check cache first
  const cached = cache.get(url);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.metadata;
  }

  // Check if there's already a pending request for this URL
  const pending = pendingRequests.get(url);
  if (pending) {
    return pending;
  }

  // Create the fetch promise
  const fetchPromise = (async (): Promise<UrlMetadata> => {
    try {
      const response = await api.get<UrlMetadata>('/utils/url-metadata', {
        params: { url },
      });

      if (response.success && response.data) {
        // Cache the result
        cache.set(url, {
          metadata: response.data,
          expiresAt: Date.now() + CACHE_TTL_MS,
        });
        return response.data;
      }

      // Return default metadata on error
      return getDefaultMetadata(url);
    } catch {
      return getDefaultMetadata(url);
    } finally {
      // Remove from pending requests
      pendingRequests.delete(url);
    }
  })();

  // Track the pending request
  pendingRequests.set(url, fetchPromise);

  return fetchPromise;
}

/**
 * Get default metadata when fetch fails.
 */
function getDefaultMetadata(url: string): UrlMetadata {
  let domain: string;
  try {
    domain = new URL(url).hostname;
  } catch {
    domain = url;
  }

  return {
    url,
    title: null,
    description: null,
    favicon: null,
    domain,
  };
}
